import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";
import { ProgressRing } from "@/components/ProgressRing";
import { toast } from "sonner";
import { Flame, BookOpen, Trophy, Bookmark, Sparkles, BrainCircuit, Scale, Map, ArrowRight, Crown, Target, Swords, Award, Loader2, Zap, Medal } from "lucide-react";

function StatCard({ icon: I, label, value, accent, testid }) {
  return (
    <div className="eco-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        <I size={18} style={{ color: accent }} strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl font-bold text-white mt-3">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState("");
  const [editing, setEditing] = useState(false);
  const [savingGoals, setSavingGoals] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => { setData(r.data); setGoals(r.data.goals || ""); }).catch(() => {});
  }, []);

  const saveGoals = async () => {
    setSavingGoals(true);
    try { await api.post("/coach/goals", { goals }); setEditing(false); toast.success("ECO will remember your goals"); }
    catch (e) { toast.error("Could not save"); } finally { setSavingGoals(false); }
  };

  const greet = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; })();

  const quickActions = [
    { to: "/mentor", label: "Ask ECO", desc: "Your AI mentor", icon: Sparkles, color: "#0055FF" },
    { to: "/decision-lab", label: "Decision Lab", desc: "Pro engine", icon: Scale, color: "#10B981" },
    { to: "/roadmap", label: "Founder Roadmap", desc: "Pro tool", icon: Map, color: "#F59E0B" },
    { to: "/iq-test", label: "Business IQ", desc: "Test skills", icon: BrainCircuit, color: "#A855F7" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">{greet}</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">{user?.name?.split(" ")[0] || "Founder"} 👋</h1>
        <p className="text-slate-400 mt-2">Your entrepreneur operating system at a glance.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-streak" icon={Flame} label="Day streak" value={data?.streak ?? 0} accent="#F59E0B" />
        <StatCard testid="stat-lessons" icon={BookOpen} label="Lessons done" value={data?.lessons_completed ?? 0} accent="#0055FF" />
        <StatCard testid="stat-iq" icon={BrainCircuit} label="Business IQ" value={data?.iq?.overall != null ? `${data.iq.overall}%` : "—"} accent="#A855F7" />
        <StatCard testid="stat-neg" icon={Swords} label="Negotiation" value={data?.negotiation?.score != null ? `${data.negotiation.score}` : "—"} accent="#EF4444" />
      </div>

      {/* Goals (Coach memory) */}
      <div className="eco-card p-6" data-testid="dashboard-goals">
        <div className="flex items-center justify-between mb-2">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Target size={18} className="text-[#4d8bff]" /> Your goals</h2>
          {!editing && <button onClick={() => setEditing(true)} data-testid="edit-goals-btn" className="text-sm text-[#4d8bff] hover:underline">{goals ? "Edit" : "Set goals"}</button>}
        </div>
        {editing ? (
          <div className="space-y-3">
            <textarea data-testid="dashboard-goals-input" value={goals} onChange={(e) => setGoals(e.target.value)} rows={2} placeholder="e.g., Build & launch a profitable AI SaaS this year." className="eco-input resize-none" />
            <div className="flex gap-2">
              <button onClick={saveGoals} disabled={savingGoals} data-testid="dashboard-goals-save" className="btn-accent text-sm">{savingGoals ? <Loader2 size={15} className="animate-spin" /> : "Save"}</button>
              <button onClick={() => setEditing(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-slate-400 text-sm">{goals || "Tell ECO your goals so it can personalize your mentoring and recommendations."}</p>
        )}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="font-display text-lg font-semibold text-white mb-4">Quick actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((a) => (
            <Link key={a.to} to={a.to} data-testid={`quick-${a.to.replace("/", "")}`} className="eco-card eco-card-hover p-5 group">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${a.color}1a`, border: `1px solid ${a.color}33` }}>
                <a.icon size={20} style={{ color: a.color }} strokeWidth={1.5} />
              </div>
              <div className="font-display font-semibold text-white">{a.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{a.desc}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Challenges & XP */}
      <div data-testid="dashboard-challenges">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Trophy size={18} className="text-amber-400" /> Challenges & XP</h2>
          <Link to="/challenges" data-testid="dashboard-challenges-link" className="text-sm text-[#4d8bff] hover:underline flex items-center gap-1">All challenges <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="eco-card p-5" data-testid="dashboard-xp">
            <div className="flex items-center justify-between"><span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Total XP</span><Zap size={18} className="text-amber-400" /></div>
            <div className="font-display text-3xl font-bold text-white mt-3">{data?.total_xp ?? 0}</div>
          </div>
          <div className="eco-card p-5" data-testid="dashboard-badges">
            <div className="flex items-center justify-between mb-3"><span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Badges</span><Medal size={18} className="text-amber-400" /></div>
            {data?.badges?.length ? (
              <div className="flex flex-wrap gap-2">
                {data.badges.map((b) => (
                  <div key={b.id} title={b.challenge} className="h-9 w-9 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                    <Icon name={b.icon} size={17} className="text-amber-400" />
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-500">Complete a challenge to earn your first badge.</p>}
          </div>
          {data?.active_challenges?.[0] ? (
            <Link to={`/challenges/${data.active_challenges[0].id}`} data-testid="dashboard-active-challenge" className="eco-card eco-card-hover p-5 flex items-center gap-4">
              <ProgressRing value={data.active_challenges[0].percent} size={48} />
              <div className="min-w-0">
                <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Continue</div>
                <div className="text-sm font-semibold text-white truncate mt-0.5">{data.active_challenges[0].title}</div>
              </div>
            </Link>
          ) : (
            <Link to="/challenges" className="eco-card eco-card-hover p-5 flex items-center gap-3" data-testid="dashboard-start-challenge">
              <div className="h-10 w-10 rounded-xl bg-[#0055FF]/10 border border-[#0055FF]/20 flex items-center justify-center"><Trophy size={18} className="text-[#4d8bff]" /></div>
              <div><div className="text-sm font-semibold text-white">Start a challenge</div><div className="text-xs text-slate-500">Earn XP & badges</div></div>
            </Link>
          )}
        </div>
      </div>

      {/* Achievements */}
      {data?.achievements?.length > 0 && (
        <div data-testid="dashboard-achievements">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><Award size={18} className="text-amber-400" /> Achievements</h2>
          <div className="flex flex-wrap gap-3">
            {data.achievements.map((a) => (
              <div key={a.id} className="eco-card px-4 py-3 flex items-center gap-2.5" data-testid={`achievement-${a.id}`}>
                <Icon name={a.icon} size={18} className="text-amber-400" />
                <span className="text-sm font-medium text-white">{a.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pro banner */}
      {!data?.is_pro && (
        <div className="eco-card glow-grid p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="dashboard-pro-banner">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0"><Crown size={20} className="text-amber-400" /></div>
            <div>
              <h3 className="font-display text-lg font-semibold text-white">Unlock the full Entrepreneur OS</h3>
              <p className="text-sm text-slate-400 mt-1">Decision Lab, Deal Analyzer, Founder Roadmaps, Negotiation Masterclass, the Framework Library and unlimited ECO.</p>
            </div>
          </div>
          <Link to="/pricing" data-testid="dashboard-upgrade-btn" className="btn-accent shrink-0">Upgrade</Link>
        </div>
      )}

      {/* Paths + recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">Continue learning</h2>
            <Link to="/university" className="text-sm text-[#4d8bff] hover:underline flex items-center gap-1">All paths <ArrowRight size={14} /></Link>
          </div>
          {data?.paths?.length ? (
            <div className="space-y-3">
              {data.paths.map((p) => (
                <Link key={p.id} to={`/university/${p.id}`} data-testid={`dash-path-${p.id}`} className="eco-card eco-card-hover p-4 flex items-center gap-4">
                  <ProgressRing value={p.percent} size={52} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-white">{p.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.done} of {p.total} lessons complete</div>
                  </div>
                  <Icon name={p.icon} size={20} className="text-slate-600" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="eco-card p-8 text-center">
              <p className="text-slate-400 text-sm">No paths started yet.</p>
              <Link to="/university" className="btn-secondary text-sm mt-4 inline-flex">Browse Founder University</Link>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold text-white">Recommended for you</h2>
          <div className="space-y-3">
            {data?.recommendations?.slice(0, 5).map((r) => (
              <Link key={r.category_id} to={`/knowledge/${r.category_id}`} data-testid={`dash-rec-${r.category_id}`} className="eco-card eco-card-hover p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${r.color}1a` }}>
                  <Icon name={r.icon} size={18} style={{ color: r.color }} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{r.topic}</div>
                  <div className="text-xs text-slate-500">{r.category_name} · {r.level}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
